"use client";

// Indeks HADIAH PEMBELIAN untuk SELURUH storefront (rencana §13.3, Tahap 2A).
//
// Idiom `PromoContext`: dibaca server SEKALI (root layout,
// `fetchActiveGiftIndexServer`, cache ~60 dtk) lalu diedarkan — chip "Gratis
// hadiah" di kartu produk, seksi "Hadiah Pembelian" `/promo`, dan blok halaman
// produk tak pernah query per kartu. Level pembeli dinilai di sini (server tak
// tahu pembelinya): tamu = slot pelanggan umum.

import { createContext, useContext, useMemo, type ReactNode } from "react";

import { useCustomerGroup } from "@/context/CustomerContext";
import { bundleCustomerSlot } from "@/lib/bundle";
import { giftOwnerCandidates, type GiftProductRef, type GiftRule } from "@/lib/gift";
import { EMPTY_GIFT_DATA, type StorefrontGiftData } from "@/lib/giftCart";
import { giftChipFor, giftIndexToData, giftPromoCards, type GiftIndex, type GiftPromoCard } from "@/lib/giftIndex";

type GiftIndexValue = {
  data: StorefrontGiftData;
  /** Jam server saat halaman dibuat — SSR & render klien pertama memakai jam yang sama. */
  now: Date;
  /** Slot level pembeli (`bundleCustomerSlot`); null = tamu / pelanggan umum. */
  slot: number | null;
  /**
   * Aturan berjalan & berhak level pembeli ini, urut umur (`giftOwnerCandidates`)
   * — dihitung SEKALI; chip kartu & blok halaman produk memakainya ulang.
   */
  candidates: readonly GiftRule[];
};

const NO_RULES: readonly GiftRule[] = [];

const GiftIndexContext = createContext<GiftIndexValue>({
  data: EMPTY_GIFT_DATA,
  now: new Date(0),
  slot: null,
  candidates: NO_RULES
});

export function GiftIndexProvider({
  index,
  serverNow,
  children
}: {
  index: GiftIndex | null | undefined;
  serverNow?: string | null;
  children: ReactNode;
}) {
  const slot = bundleCustomerSlot(useCustomerGroup());
  const data = useMemo(() => giftIndexToData(index), [index]);
  // Aturan sudah disaring "berjalan" saat dibaca server; jam server dipakai
  // supaya HTML server & render klien pertama identik (idiom PromoProvider).
  const now = useMemo(() => (serverNow ? new Date(serverNow) : new Date()), [serverNow]);
  const candidates = useMemo(
    () => (data.rules.length ? giftOwnerCandidates(data.rules, { slot, now }) : NO_RULES),
    [data, slot, now]
  );
  const value = useMemo(() => ({ data, now, slot, candidates }), [data, now, slot, candidates]);
  return <GiftIndexContext.Provider value={value}>{children}</GiftIndexContext.Provider>;
}

export function useGiftIndex(): GiftIndexValue {
  return useContext(GiftIndexContext);
}

/**
 * Chip "Gratis hadiah" untuk kartu produk ini? Dicocokkan dari id, merek,
 * kategori, dan tipe yang sudah ada di data kartu (aturan bertarget, mig 8780;
 * "semua produk" tanpa chip — H29).
 */
export function useGiftChip(product: GiftProductRef): boolean {
  const { data, candidates } = useContext(GiftIndexContext);
  const { id, brand, category, productType } = product;
  return useMemo(
    () => (candidates.length ? giftChipFor(data, candidates, { id, brand, category, productType }) : false),
    [data, candidates, id, brand, category, productType]
  );
}

/** Kartu seksi "Hadiah Pembelian" `/promo` untuk pembeli ini. */
export function useGiftPromoCards(): GiftPromoCard[] {
  const { data, slot, now } = useContext(GiftIndexContext);
  return useMemo(() => (data.rules.length ? giftPromoCards(data, slot, now) : []), [data, slot, now]);
}
